import {
  ActionIcon,
  Badge,
  Box,
  Menu,
  Skeleton,
  Table,
  Text,
  TextInput,
  createStyles,
} from "@mantine/core";
import {
  IconCalendar,
  IconCircleCheck,
  IconDotsVertical,
  IconListCheck,
  IconSearch,
  IconUser,
} from "@tabler/icons-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import NewTaskModal from "../../../components/ProjectModal";
import CustomPagination from "../../../components/Pagination/CustomPagination";
import { useDisclosure } from "@mantine/hooks";
import { isStaff } from "../../../http";
import { getDashboardProjectTasksSkypoint } from "../../../services/dashboard";
import {
  deleteSkypointProjectTask,
  updateSkypointProjectTask,
} from "../../../services/skypointProject";
import useDebounce from "../../../utils/useDebounce";

const useStyles = createStyles((theme) => ({
  container: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: 12,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    // Locked to match the Pending Actions panel so both columns line up.
    height: 640,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    borderBottom: "1px solid #F2F4F7",
    gap: 16,
    flexWrap: "wrap",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
  },
  iconBox: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "#F4EBFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  title: {
    fontFamily: "Inter",
    fontSize: 15,
    fontWeight: 600,
    color: "#101828",
  },
  countBadge: {
    fontFamily: "Inter",
    fontWeight: 600,
  },
  search: {
    flex: 1,
    maxWidth: 280,
    minWidth: 180,
  },
  scrollWrap: {
    flex: 1,
    overflowY: "auto",
    minHeight: 0,
    "::-webkit-scrollbar": { width: 6 },
    "::-webkit-scrollbar-thumb": {
      background: "#D0D5DD",
      borderRadius: 3,
    },
  },
  table: {
    "& thead tr th": {
      position: "sticky",
      top: 0,
      backgroundColor: "#F9FAFB",
      fontFamily: "Inter",
      fontSize: 12,
      fontWeight: 600,
      color: "#667085",
      textTransform: "uppercase",
      letterSpacing: 0.4,
      borderBottom: "1px solid #EAECF0",
      zIndex: 1,
    },
    "& tbody tr": {
      transition: "background 100ms ease",
      "&:hover": { background: "#FAFBFC" },
    },
    "& tbody tr td": {
      fontFamily: "Inter",
      fontSize: 13,
      color: "#101828",
      borderBottom: "1px solid #F9FAFB",
      verticalAlign: "middle",
    },
    "& tbody tr:last-of-type td": { borderBottom: "none" },
  },
  taskCell: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  taskTitle: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    color: "#101828",
  },
  taskSubtitle: {
    fontFamily: "Inter",
    fontSize: 11.5,
    color: "#667085",
  },
  metaCell: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontFamily: "Inter",
    fontSize: 12.5,
    color: "#475467",
  },
  statusPill: {
    fontFamily: "Inter",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  empty: {
    padding: "48px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    color: "#667085",
    fontFamily: "Inter",
  },
  emptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: "#F4F0FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#101828",
  },
  emptyBody: {
    fontSize: 12,
    color: "#667085",
    textAlign: "center",
  },
  footer: {
    borderTop: "1px solid #F2F4F7",
    padding: "8px 12px",
  },
}));

const STATUS_COLOR = {
  done: "green",
  "in progress": "blue",
  "in review": "violet",
  "to do": "gray",
};

export function ActivityTable() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.profile);
  const {
    dashboardTasksSkypoint,
    dashboardTasksSkypointLoading,
    dashboardTasksCountSkypoint,
  } = useSelector((state) => state?.dashboard);
  const { skypointProjectStatus } = useSelector(
    (state) => state?.skypointProject
  );

  const doneStatus = (skypointProjectStatus || []).find(
    (s) => s?.status?.toLowerCase() === "done"
  );

  const [selectedTask, setSelectedTask] = useState(null);
  const [mode, setMode] = useState("view");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [taskModalOpened, { open: taskModalOpen, close: taskModalClose }] =
    useDisclosure(false);

  useEffect(() => {
    getDashboardProjectTasksSkypoint(currentPage, debouncedSearch || "");
  }, [debouncedSearch, currentPage]);

  const handleTaskEditClick = (data) => {
    setSelectedTask(data);
    setMode("edit");
    taskModalOpen();
  };

  const handleTaskViewClick = (data) => {
    setSelectedTask(data);
    setMode("view");
    taskModalOpen();
  };

  const handleTaskDeleteClick = async (data) => {
    const ok = await swal({
      title: "Delete this task?",
      text: "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (ok) deleteSkypointProjectTask(data?.id);
  };

  const handleMarkAsDone = async (data) => {
    if (!doneStatus?.id) return;
    await updateSkypointProjectTask({
      id: data?.id,
      status: doneStatus.id,
    });
  };

  const totalCount = dashboardTasksCountSkypoint || 0;
  const rows = dashboardTasksSkypoint || [];
  const canEdit = !isStaff(profile?.user?.role);

  return (
    <Box className={classes.container}>
      <NewTaskModal
        opened={taskModalOpened}
        onClose={() => {
          setSelectedTask(null);
          setMode("add");
          taskModalClose();
        }}
        mode={mode}
        selectedTask={selectedTask}
      />

      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box className={classes.iconBox}>
            <IconListCheck size={18} color="#7F56D9" />
          </Box>
          <Text className={classes.title}>To-do List</Text>
          {!dashboardTasksSkypointLoading && totalCount > 0 && (
            <Badge
              size="sm"
              variant="light"
              color="violet"
              className={classes.countBadge}
            >
              {totalCount}
            </Badge>
          )}
        </Box>
        <TextInput
          className={classes.search}
          icon={<IconSearch size={14} />}
          placeholder="Search task or assignee…"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          radius="md"
          size="sm"
        />
      </Box>

      <Box className={classes.scrollWrap}>
        {dashboardTasksSkypointLoading ? (
          <Box p="md">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={40} radius="sm" mb={8} />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Box className={classes.empty}>
            <Box className={classes.emptyIcon}>
              <IconListCheck size={22} color="#7F56D9" />
            </Box>
            <Text className={classes.emptyTitle}>
              {searchTerm ? "No tasks match your search" : "No tasks yet"}
            </Text>
            <Text className={classes.emptyBody}>
              {searchTerm
                ? "Try a different keyword."
                : "Project tasks assigned to you will appear here."}
            </Text>
          </Box>
        ) : (
          <Table className={classes.table} verticalSpacing="sm">
            <thead>
              <tr>
                <th>Task</th>
                <th style={{ width: 140 }}>Due Date</th>
                <th style={{ width: 160 }}>Assigned to</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 48 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((data) => {
                const statusLabel = data?.status?.status || "—";
                const statusColor =
                  STATUS_COLOR[statusLabel.toLowerCase()] || "gray";
                const isAlreadyDone =
                  statusLabel.toLowerCase() === "done";
                return (
                  <tr
                    key={data.id || data._id}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleTaskViewClick(data)}
                  >
                    <td>
                      <Box className={classes.taskCell}>
                        <Text className={classes.taskTitle}>
                          {data?.title || "Untitled task"}
                        </Text>
                        {data?.project?.title && (
                          <Text className={classes.taskSubtitle}>
                            {data.project.title}
                          </Text>
                        )}
                      </Box>
                    </td>
                    <td>
                      <Box className={classes.metaCell}>
                        {data?.date ?
                        <IconCalendar size={13} color="#98A2B3" />
                        : null}
                        {data?.date
                          ? moment(data.date).format("MMM D, YYYY")
                          : ""}
                      </Box>
                    </td>
                    <td>
                      <Box className={classes.metaCell}>
                        <IconUser size={13} color="#98A2B3" />
                        {data?.assignedTo?.name || "Unassigned"}
                      </Box>
                    </td>
                    <td>
                      <Badge
                        variant="light"
                        color={statusColor}
                        radius="sm"
                        className={classes.statusPill}
                      >
                        {statusLabel}
                      </Badge>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Menu shadow="md" width={160} withinPortal position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {!isAlreadyDone && doneStatus?.id && (
                            <Menu.Item
                              color="green"
                              icon={<IconCircleCheck size={14} />}
                              onClick={() => handleMarkAsDone(data)}
                            >
                              Mark as done
                            </Menu.Item>
                          )}
                          <Menu.Item onClick={() => handleTaskViewClick(data)}>
                            View
                          </Menu.Item>
                          {canEdit && (
                            <Menu.Item onClick={() => handleTaskEditClick(data)}>
                              Edit
                            </Menu.Item>
                          )}
                          {canEdit && (
                            <Menu.Item
                              color="red"
                              onClick={() => handleTaskDeleteClick(data)}
                            >
                              Delete
                            </Menu.Item>
                          )}
                        </Menu.Dropdown>
                      </Menu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Box>

      {!dashboardTasksSkypointLoading && totalCount > 10 && (
        <Box className={classes.footer}>
          <CustomPagination
            data={totalCount}
            itemsPerPage={10}
            currentPage={currentPage}
            handlePageChange={setCurrentPage}
          />
        </Box>
      )}
    </Box>
  );
}
